import { useState, useMemo, useEffect } from 'react'
import { Box, HStack, VStack, Badge, IconButton, Icon, Text, Collapsible } from '@chakra-ui/react';
import { FiMenu, FiTrash2, FiChevronDown, FiChevronRight, FiGitBranch, FiCornerDownLeft } from 'react-icons/fi'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ScriptElementType,
  ELEMENT_TYPE_LABELS,
  ELEMENT_TYPE_COLORS,
} from '../../types/script'
import type {
  ScriptElementFormState,
  TagPickerDto,
  QuestionPickerDto,
  ScriptLane,
} from '../../types/script'
import { TextElementFields } from './TextElementFields'
import { SurveyQuestionElementFields } from './SurveyQuestionElementFields'
import { TagAssignmentElementFields } from './TagAssignmentElementFields'
import { ConditionalBranchElementFields } from './ConditionalBranchElementFields'
import { getElementSummary, getAllElements, getMergingLanes } from './laneUtils'

interface LaneElementCardProps {
  element: ScriptElementFormState
  lanes: ScriptLane[]
  stepNumber: number
  isLast: boolean
  laneColorScheme: string
  onUpdate: (updates: Partial<ScriptElementFormState>) => void
  onRemove: () => void
  onCreateBranch?: (branchType: string, branchKey: string, label: string) => void
  tagOptions: TagPickerDto[]
  questionOptions: QuestionPickerDto[]
}

export function LaneElementCard({
  element,
  lanes,
  stepNumber,
  isLast,
  laneColorScheme,
  onUpdate,
  onRemove,
  onCreateBranch,
  tagOptions,
  questionOptions,
}: LaneElementCardProps) {
  const [isExpanded, setIsExpanded] = useState(element.isNew ?? false)

  // Clear the transient "new" flag once, after the initial expanded mount.
  useEffect(() => {
    if (element.isNew) onUpdate({ isNew: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Memoize allElements to prevent unnecessary re-renders of field components
  const allElements = useMemo(() => getAllElements(lanes), [lanes])

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.clientId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const borderColor = `${ELEMENT_TYPE_COLORS[element.elementType]}.400`
  const summary = getElementSummary(element, { tagOptions, questionOptions })

  // Branch lanes that originate from this element
  const childBranchLanes = lanes.filter(
    (l) => l.sourceElementClientId === element.clientId
  )

  // Branch lanes that merge back into this element
  const mergingLanes = getMergingLanes(lanes, element.clientId)

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-client-id={element.clientId}
      position="relative"
      _after={
        !isLast
          ? {
              content: '""',
              position: 'absolute',
              bottom: '-7px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              borderTop: '6px solid',
              borderTopColor: `${laneColorScheme}.300`,
            }
          : undefined
      }
    >
      <Box
        bg="surface"
        borderRadius="md"
        shadow="sm"
        borderWidth="1px"
        borderLeftWidth="4px"
        borderLeftColor={borderColor}
        overflow="hidden"
        borderTopWidth={mergingLanes.length > 0 ? '3px' : '1px'}
        borderTopColor={mergingLanes.length > 0 ? `${mergingLanes[0].colorScheme}.400` : undefined}
        borderTopStyle={mergingLanes.length > 0 ? 'dashed' : undefined}
      >
        {/* Merge target indicator - shown when branch lanes rejoin at this element */}
        {mergingLanes.length > 0 && (
          <HStack px={3} py={1} bg="gray.50" gap={1} flexWrap="wrap">
            <Icon as={FiCornerDownLeft} color="gray.400" boxSize={3} />
            <Text fontSize="2xs" color="gray.500">
              Rejoins from:
            </Text>
            {mergingLanes.map((ml) => (
              <Badge
                key={ml.id}
                colorPalette={ml.colorScheme}
                fontSize="2xs"
                variant="outline"
              >
                {ml.label}
              </Badge>
            ))}
          </HStack>
        )}

        {/* Compact header - always visible */}
        <HStack
          px={3}
          py={2}
          gap={2}
          cursor="pointer"
          _hover={{ bg: 'gray.50' }}
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon
            as={FiMenu}
            color="gray.400"
            cursor="grab"
            flexShrink={0}
            onClick={(e) => e.stopPropagation()}
            {...attributes}
            {...listeners}
          />
          <Text fontSize="xs" color="gray.400" fontWeight="bold" flexShrink={0}>
            #{stepNumber}
          </Text>
          <Badge
            colorPalette={ELEMENT_TYPE_COLORS[element.elementType]}
            fontSize="2xs"
            flexShrink={0}
          >
            {ELEMENT_TYPE_LABELS[element.elementType]}
          </Badge>
          <Text fontSize="sm" color="gray.600" lineClamp={1} flex={1}>
            {summary}
          </Text>
          <Icon
            as={isExpanded ? FiChevronDown : FiChevronRight}
            color="gray.400"
            flexShrink={0}
          />
          <IconButton
            aria-label="Remove element"
            size="xs"
            variant="ghost"
            colorPalette="red"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}><FiTrash2 /></IconButton>
        </HStack>

        {/* Branch origin badges - shown when this element has child branch lanes */}
        {childBranchLanes.length > 0 && (
          <HStack px={3} py={1} gap={1} flexWrap="wrap" bg="gray.50" borderTopWidth="1px" borderTopColor="gray.100">
            <Icon as={FiGitBranch} color="gray.400" boxSize={3} />
            {childBranchLanes.map((bl) => (
              <Badge
                key={bl.id}
                colorPalette={bl.colorScheme}
                fontSize="2xs"
                variant="subtle"
              >
                {bl.label}
              </Badge>
            ))}
          </HStack>
        )}

        {/* Expanded fields */}
        <Collapsible.Root open={isExpanded}>
          <Collapsible.Content>
            <VStack gap={3} align="stretch" px={3} pb={3} pt={1}>
              {element.elementType === ScriptElementType.Text && (
                <TextElementFields
                  textContent={element.textContent ?? ''}
                  textContentSpanish={element.textContentSpanish ?? ''}
                  onChange={(field, value) => onUpdate({ [field]: value })}
                />
              )}

              {element.elementType === ScriptElementType.SurveyQuestion && (
                <SurveyQuestionElementFields
                  surveyQuestionId={element.surveyQuestionId}
                  questionOptions={questionOptions}
                  currentClientId={element.clientId}
                  onChange={onUpdate}
                  onCreateBranch={onCreateBranch}
                  lanes={lanes}
                />
              )}

              {element.elementType === ScriptElementType.TagAssignment && (
                <TagAssignmentElementFields
                  tagId={element.tagId}
                  tagOptions={tagOptions}
                  onChange={(tagId) => onUpdate({ tagId })}
                />
              )}

              {element.elementType === ScriptElementType.ConditionalBranch && (
                <ConditionalBranchElementFields
                  element={element}
                  allElements={allElements}
                  onChange={onUpdate}
                  tagOptions={tagOptions}
                  questionOptions={questionOptions}
                  onCreateBranch={onCreateBranch}
                  lanes={lanes}
                />
              )}
            </VStack>
          </Collapsible.Content>
        </Collapsible.Root>
      </Box>
    </Box>
  );
}
